/**
 * Hook para generar las clases CSS del botón
 *
 * Este hook centraliza la lógica de generación de clases
 * usando class-variance-authority y las variantes de color.
 */

import { useMemo } from 'react';
import { cn } from '@/shared/utils/cn';
import { createButtonVariants } from '../styles/buttonVariants';
import { ButtonColors, ButtonTheme } from '@/shared/types/types';

interface UseButtonClassesOptions {
  theme: ButtonTheme;
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
  color?: ButtonColors;
  rounded?: boolean;
  fullWidth: boolean;
  pill?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'pill';
  clickable: boolean;
  borderable?: boolean;
  whiteBackground: boolean;
  active: boolean;
  square: boolean;
  icon: boolean;
  children: boolean;
  shouldShowBadge: boolean;
  showShadow: boolean;
  pointer: boolean;
  transition: boolean;
  classNameId: string;
  className?: string;
  isIconVariant: boolean;
  defaultSizeIcon: boolean;
}

/**
 * Hook que genera las clases CSS del botón
 *
 * @param options - Opciones para generar las clases
 * @returns Clases CSS del botón
 */
export const useButtonClasses = ({
  theme,
  size,
  color,
  rounded,
  fullWidth,
  pill,
  clickable,
  borderable,
  whiteBackground,
  active,
  square,
  icon,
  children,
  shouldShowBadge,
  showShadow,
  pointer,
  transition,
  classNameId,
  className,
  isIconVariant,
  defaultSizeIcon,
}: UseButtonClassesOptions) => {
  const currentDefaultSizeIcon = isIconVariant ? true : defaultSizeIcon;
  const currentSizeIcon = isIconVariant ? 'xs' : size || 'md';

  // `rounded` añade `ui-rounded-control-pill`; `pill` añade radios por tamaño (p. ej. sm → ui-rounded-control-sm).
  // No deben combinarse: el default de pill es 'sm', así que con rounded=true habría dos clases de radio.
  const effectivePill = rounded ? 'none' : pill;

  const buttonVariants = useMemo(
    () =>
      createButtonVariants(
        theme,
        clickable,
        borderable,
        whiteBackground,
        active,
        square,
        icon,
        children,
        shouldShowBadge,
        showShadow,
        pointer,
        transition,
        classNameId,
      ),
    [
      theme,
      clickable,
      borderable,
      whiteBackground,
      active,
      square,
      icon,
      children,
      shouldShowBadge,
      showShadow,
      pointer,
      transition,
      classNameId,
    ],
  );

  const buttonClasses = useMemo(
    () =>
      buttonVariants({
        size: currentSizeIcon,
        color,
        rounded,
        fullWidth,
        theme,
        pill: effectivePill,
        className: cn(className),
      }),
    [buttonVariants, currentSizeIcon, color, rounded, fullWidth, theme, effectivePill, className],
  );

  return {
    buttonClasses,
    currentSizeIcon,
    currentDefaultSizeIcon,
  };
};
